import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Lock, Scale, Shield } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { S9_RAO } from '../../landing-templates/t04-dala-orbit/t04LandingShared';
import { T05ProductShell } from './T05ProductShell';
import { T05LegalModals, type T05LegalModalId } from './T05LegalModals';
import { T05_LEGAL_STAND, T05_LEGAL_TRADEMARK_FOOTNOTE, T05_S9_MARKETING } from './t05LegalContent';
import './t05-product.css';

interface ProductRechtProps {
  onLoginClick?: () => void;
}

type LegalCategory = {
  id: T05LegalModalId;
  label: string;
  hint: string;
  icon: LucideIcon;
  kicker: string;
};

const LEGAL_CATEGORIES: LegalCategory[] = [
  {
    id: 'agb',
    label: 'Verträge & AGB',
    hint: 'Leistungsumfang, Haftung, SLA 98 %, Kündigung, Gerichtsstand Österreich. Inkl. AVV nach Art. 28 DSGVO.',
    icon: FileText,
    kicker: '§ 1–8',
  },
  {
    id: 'privacy',
    label: 'Datenschutz & AVV',
    hint: 'Welche Daten wir verarbeiten, Sub-Auftragsverarbeiter (Hetzner, Stripe, Self-Hosted Matrix) und Ihre Rechte nach Art. 15–21 DSGVO.',
    icon: Shield,
    kicker: 'DSGVO',
  },
  {
    id: 'impressum',
    label: 'Impressum & Compliance',
    hint: 'Medieninhaber, UID, Gewerbe & Aufsicht, Kontakt — gemäß österreichischem Medien- und E-Commerce-Recht.',
    icon: Scale,
    kicker: '§ 25 MedienG',
  },
];

const COMPLIANCE_FACTS = [
  'EU-Server Frankfurt',
  'Self-Hosted Edge',
  `${S9_RAO} · Verschwiegenheit`,
  'DSGVO Art. 28 AVV',
  'Kein CLOUD Act',
];

export const ProductRecht: React.FC<ProductRechtProps> = ({ onLoginClick }) => {
  const [activeModal, setActiveModal] = useState<T05LegalModalId>(null);

  useRevealOnScroll();

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'agb') setActiveModal('agb');
    else if (hash === 'datenschutz') setActiveModal('privacy');
    else if (hash === 'impressum') setActiveModal('impressum');
  }, []);

  const openModal = (id: T05LegalModalId) => {
    if (!id) return;
    setActiveModal(id);
    const hash = id === 'agb' ? 'agb' : id === 'privacy' ? 'datenschutz' : 'impressum';
    window.history.replaceState(null, '', `/recht#${hash}`);
  };

  const closeModal = () => {
    setActiveModal(null);
    window.history.replaceState(null, '', '/recht');
  };

  return (
    <T05ProductShell productLabel="Rechtliches" onLoginClick={onLoginClick}>
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Transparenz · Vertrauen</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Rechtliches
          <span className="t05-headline__accent"> &amp; Vertrauen</span>
        </h1>
        <p className="t05-lead reveal-up">
          Verträge, Datenschutz und Impressum — eine Übersicht für Ihre Kanzlei. DSGVO, AVV und AGB sind bei uns
          kein Kleingedrucktes, sondern Teil der Architektur.
        </p>
      </header>

      <section className="t05-trust-strip t05-container reveal-up" aria-label="Compliance-Merkmale">
        <div className="t05-trust-strip__row">
          {COMPLIANCE_FACTS.map((fact, i) => (
            <React.Fragment key={fact}>
              {i > 0 && <span className="t05-trust-strip__sep" aria-hidden>·</span>}
              <span className="t05-trust-strip__item">{fact}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="t05-prod-section t05-container" aria-labelledby="recht-s9-heading">
        <div className="t05-rao-card reveal-up">
          <div className="t05-rao-card__copy">
            <p className="t05-kicker">{S9_RAO}</p>
            <h2 id="recht-s9-heading" className="t05-title">
              {T05_S9_MARKETING.title}
            </h2>
            <p className="t05-subtitle t05-section__header--left">{T05_S9_MARKETING.lead}</p>
            <ul className="t05-checklist">
              {T05_S9_MARKETING.bullets.map((item) => (
                <li key={item}>
                  <Lock className="t05-checklist__icon" size={18} aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
            <p className="t05-rao-card__note">{T05_S9_MARKETING.avvNote}</p>
            <div className="t05-cta-actions" style={{ marginTop: '1.25rem' }}>
              <button type="button" className="t05-btn t05-btn--primary t05-btn--sm" onClick={() => openModal('agb')}>
                AGB &amp; AVV öffnen
              </button>
            </div>
          </div>
          <div className="t05-product-panel t05-rao-card__panel">
            <div className="t05-product-panel__bar" aria-hidden>
              <span className="t05-product-panel__dot" />
              <span className="t05-product-panel__dot" />
              <span className="t05-product-panel__dot" />
            </div>
            <p className="t05-product-panel__title">orbis://compliance/rao</p>
            <div className="t05-product-panel__grid">
              <div className="t05-product-panel__row">
                <Shield size={16} aria-hidden />
                <span>Self-Hosted Edge</span>
                <span className="t05-product-panel__badge">EU</span>
              </div>
              <div className="t05-product-panel__row">
                <Lock size={16} aria-hidden />
                <span>AVV Art. 28 DSGVO</span>
                <span className="t05-product-panel__badge">Vertrag</span>
              </div>
              <div className="t05-product-panel__row">
                <FileText size={16} aria-hidden />
                <span>Schriftliche Bestätigung</span>
                <span className="t05-product-panel__badge">Optional</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="t05-prod-section t05-container" aria-labelledby="recht-docs-heading">
        <div className="t05-legal reveal-up">
          <div className="t05-legal__header">
            <Shield size={22} className="t05-legal__icon" aria-hidden />
            <h2 id="recht-docs-heading" className="t05-title">
              Dokumente &amp; Verträge
            </h2>
            <p className="t05-subtitle t05-section__header--left">
              Wir sind stolz darauf, diese Dokumente herzuzeigen. Vollständige Rechtstexte — Stand{' '}
              {T05_LEGAL_STAND}.
            </p>
          </div>

          <div className="t05-legal__grid">
            {LEGAL_CATEGORIES.map((cat) => (
              <button
                key={cat.label}
                type="button"
                className="t05-legal__card t05-legal__card--button"
                onClick={() => openModal(cat.id)}
              >
                <cat.icon size={20} className="t05-legal__icon" aria-hidden />
                <span className="t05-legal__kicker">{cat.kicker}</span>
                <h3>{cat.label}</h3>
                <p>{cat.hint}</p>
                <span className="t05-legal__link">
                  Dokument öffnen <ArrowRight size={14} aria-hidden />
                </span>
              </button>
            ))}
          </div>

          <p className="t05-legal__note">
            {T05_LEGAL_TRADEMARK_FOOTNOTE} · ARKORBIT AI Austria · UID ATU83006408 · Stand {T05_LEGAL_STAND}
          </p>
        </div>
      </section>

      <section className="t05-final-cta t05-container">
        <div className="t05-final-cta__band reveal-up">
          <div className="t05-final-cta__inner">
            <h2 className="t05-title">Fragen zu Verträgen oder AVV?</h2>
            <p className="t05-subtitle">
              Wir beantworten Ihre Fragen zu Infrastruktur, Datenschutz und Vertragsunterlagen persönlich.
            </p>
            <div className="t05-cta-actions">
              <a href="mailto:office@arkorbit.at" className="t05-btn t05-btn--primary">
                office@arkorbit.at
              </a>
              <Link to="/" className="t05-btn t05-btn--ghost">
                Zur Startseite
              </Link>
            </div>
          </div>
        </div>
      </section>

      <T05LegalModals active={activeModal} onClose={closeModal} />
    </T05ProductShell>
  );
};

export default ProductRecht;
